import type { DeviceHealthDeviceStat, DeviceHealthSummary } from '@platform/shared-types';

/** Three missed 5-minute heartbeats (owner decision 2026-10-04). Pinned to DEVICE_HEALTH_LIMITS by the test. */
export const OFFLINE_AFTER_MS = 15 * 60_000;

export interface HealthTile {
  id: 'devices' | 'crashFree' | 'errors' | 'tickets';
  label: string;
  value: string;
  note?: string;
  tone: 'good' | 'warn' | 'bad' | 'neutral';
}

export function offlineCount(devices: Record<string, DeviceHealthDeviceStat>, now = Date.now()): number {
  return Object.values(devices || {}).filter((d) => d.lastEventAt !== null && d.lastEventAt < now - OFFLINE_AFTER_MS).length;
}

export function buildTiles(s: DeviceHealthSummary, now = Date.now()): HealthTile[] {
  const offline = offlineCount(s.devices, now);
  const cf = s.crashFreePct;
  return [
    {
      id: 'devices', label: 'Devices reporting', value: `${s.devicesReporting} / ${s.devicesKnown}`,
      ...(offline ? { note: `${offline} offline` } : {}), tone: offline ? 'warn' : 'neutral',
    },
    {
      id: 'crashFree', label: 'Crash-free sessions', value: cf === null ? '—' : `${cf.toFixed(1)}%`,
      note: `${s.crashes} crash${s.crashes === 1 ? '' : 'es'} · ${s.sessions} sessions`,
      tone: cf === null ? 'neutral' : cf >= 99 ? 'good' : cf >= 95 ? 'warn' : 'bad',
    },
    {
      id: 'errors', label: 'Errors per hour', value: String(s.errorsPerHour),
      note: s.errorsPerSession === null ? undefined : `${s.errorsPerSession} per session`, tone: 'neutral',
    },
    {
      id: 'tickets', label: 'Open auto-tickets', value: String(s.openTickets),
      ...(s.blockingTickets ? { note: `${s.blockingTickets} blocking` } : {}),
      tone: s.blockingTickets ? 'bad' : s.openTickets ? 'warn' : 'good',
    },
  ];
}

export function sparkPoints(series: DeviceHealthSummary['series'], width: number, height: number) {
  const keys = ['crashes', 'server', 'printer', 'other'] as const;
  const max = Math.max(1, ...series.flatMap((p) => keys.map((k) => p[k])));
  const step = series.length > 1 ? width / (series.length - 1) : 0;
  const line = (k: (typeof keys)[number]) =>
    series.map((p, i) => `${Math.round(i * step * 10) / 10},${Math.round((height - (p[k] / max) * height) * 10) / 10}`).join(' ');
  return { crashes: line('crashes'), server: line('server'), printer: line('printer'), other: line('other'), max };
}

export function fmtAgo(ts: number | null, now = Date.now()): string {
  if (ts === null || !Number.isFinite(ts)) return '—';
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86_400)} d ago`;
}
